import DateDisplay from '@app/src/components/DateDisplay'
import type { DrawerViewProps } from '@app/src/components/Drawer/DrawerView'
import DrawerView from '@app/src/components/Drawer/DrawerView'
import Avatar, { AvatarSize } from '@app/src/components/Ui/Avatar'
import RiskIndexIcon from '@app/src/pages/Configurations/ConfigurationsPages/RiskScreening/RiskIndexIcons/RiskIndexIcon'
import type { RiskType } from '@app/src/types/resourceExplorer'
import { RiskIndexType } from '@app/src/types/resourceExplorer'
import { getRiskIndexSourceLink } from '@app/src/utils/riskIndexSourceLink'
import { br } from '@app/src/utils/translationMarkup'
import { Factory, OpenInNew } from '@mui/icons-material'
import { alpha, Box, ListItem, ListItemAvatar, ListItemText, Typography } from '@mui/material'
import React from 'react'
import { useIntl } from 'react-intl'

interface RiskIndexInfoDrawerViewProps extends Omit<DrawerViewProps, 'title' | 'buttons'> {
  riskType: RiskType
}

const RiskIndexInfoDrawerView: React.FC<RiskIndexInfoDrawerViewProps> = ({ riskType, ...props }) => {
  const { formatMessage, messages } = useIntl()

  const isIndustryRisk = riskType.riskIndexType === RiskIndexType.Industry
  const sourceLink = getRiskIndexSourceLink(riskType.source, messages)

  return (
    <DrawerView
      buttons={
        sourceLink
          ? [
              {
                label: formatMessage({ id: 'schemas.risk.officialWebsite' }),
                variant: 'outlined',
                size: 'large',
                target: '_blank',
                href: sourceLink,
                startIcon: <OpenInNew />,
              },
            ]
          : []
      }
      title={formatMessage({ id: 'schemas.risk.indexDetail' })}
      {...props}
    >
      <Box px={2}>
        <ListItem disablePadding sx={{ mb: 2 }}>
          <ListItemAvatar>
            <Avatar
              size={AvatarSize.XXL}
              sx={({ palette }) => ({
                backgroundColor: !isIndustryRisk ? 'white' : alpha(palette.secondary.main, 0.1),
                border: !isIndustryRisk ? `1px solid ${palette.secondary.main}` : 'none',
                mr: 2,
              })}
            >
              {isIndustryRisk ? (
                <Factory fontSize="large" color="secondary" />
              ) : (
                <RiskIndexIcon source={riskType.source} />
              )}
            </Avatar>
          </ListItemAvatar>
          <ListItemText
            primary={formatMessage({ id: `schemas.risk.riskIndexes.${riskType.source}.label` })}
            secondary={formatMessage({ id: `schemas.risk.riskIndexes.${riskType.source}.source` })}
          />
        </ListItem>

        <Typography mb={2}>
          {formatMessage(
            { id: `schemas.risk.riskIndexes.${riskType.source}.sourceDescription` },
            {
              br,
            },
          )}
        </Typography>

        <Typography mb={2}>
          {formatMessage(
            { id: `schemas.risk.riskIndexes.${riskType.source}.description` },
            {
              br,
            },
          )}
        </Typography>

        <Typography variant="subtitle2">{formatMessage({ id: `schemas.risk.riskIndexes.reviewedAt` })}</Typography>

        <Typography variant="body2" color="text.secondary">
          <DateDisplay value={riskType.reviewedAt} />
        </Typography>
      </Box>
    </DrawerView>
  )
}

export default RiskIndexInfoDrawerView
